import { requests } from '../../../utils';
import {
    AGENCY_COMPANY_BILLING_URL,
    AGENCY_COMPANY_URL,
    AGENCY_USER_URL,
    BASE_URL,
    CHANGE_PASSWORD_URL,
    CHECK_DISPLAY_NAME,
    CHECK_EMAIL,
    DELETE_USER_PROFILE_URL,
    GLOBAL_PREFERENCE_URL,
    GROUPS_URL,
    PERSONAL_INFO_URL,
    PRICE_LIST_URL,
    PROFILE_BILLING_INFO_URL,
    PROFILE_COMPANY_BILLING_URL,
    PROFILE_COMPANY_URL,
    PROFILE_COMPANY_USERS_URL,
    PROFILE_CREATE_COMPANY_USER_URL,
    PROFILE_PRIVATE_COMMENT_URL,
    PROFILE_SEND_COMMENT_URL,
    RIGHTS_AND_EXCLUSIVITY_URL,
} from '../../../utils/endpoints';
import { IChangePassword } from './components/LoginAndSecutiryModal';
import { IPersonalInfo } from './dto/IPersonalInfo';
import { ICompany, ICompanyInfoData, ICompanyUsersFilter } from './dto/ICompany';
import { ICompanyUser } from './dto/ICompanyUser';
import { IBaseModal } from './dto/IBaseModal';
import { IPriceList } from '../../../model/shared/IPriceList';
import { IRightsExclusivity } from './dto/IRights&Exclusivity';
import { IGlobalPreference } from './dto/IGlobalPreference';
import { IBillingInfo } from './dto/IBillingInfo';
import { BaseModel } from '../../../model/shared/BaseModel';
import { ICompanyUserGroup } from './dto/ICompanyUserGroup';
import { IAddGroup } from './dto/ICompanyUserGroup';
import { IDeliveryAddress } from './dto/IDeliveryAddress';

export const UserService = {
    changePassword: (body: IChangePassword) => requests.put(CHANGE_PASSWORD_URL, body),
    getGlobalPreference: (): Promise<IGlobalPreference> => requests.get(GLOBAL_PREFERENCE_URL),
    updateGlobalPreference: (body: Partial<IGlobalPreference>): Promise<IGlobalPreference> =>
        requests.put(GLOBAL_PREFERENCE_URL, body),
    getPersonalInfo: (id: number): Promise<IBaseModal<IPersonalInfo>> => requests.get(`${PERSONAL_INFO_URL}/${id}`),
    updatePersonalInfo: (body: IPersonalInfo, id: number) => requests.put(`${PERSONAL_INFO_URL}/${id}`, body),
    getCompanyInfo: (): Promise<IBaseModal<ICompany>> => requests.get(PROFILE_COMPANY_URL),
    updateCompanyBilling: (body: ICompany): Promise<IBaseModal<ICompany>> =>
        requests.put(PROFILE_COMPANY_BILLING_URL, body),
    getInfluencerBilling: (): Promise<IBillingInfo> => requests.get(PROFILE_BILLING_INFO_URL),
    updateInfluencerBilling: (body: ICompany) => requests.put(PROFILE_BILLING_INFO_URL, body),
    getCompanyUsers: (params?: any, clientRoute?: string): Promise<ICompanyUser[]> =>
        requests.get(`${PROFILE_COMPANY_USERS_URL}${clientRoute ?? ''}`, params),
    getCompanyUsersV2: (params?: ICompanyUsersFilter): Promise<BaseModel<ICompanyUser[], ICompanyInfoData>> =>
        requests.get(PROFILE_COMPANY_USERS_URL + '/v2', params),
    getUsersContracts: (params?: any): Promise<any> => requests.get(PROFILE_COMPANY_USERS_URL + '/contracts', params),
    getUsersContractsDropdown: (params?: any): Promise<any> =>
        requests.get(PROFILE_COMPANY_USERS_URL + '/contracts/dropdown', params),
    createCompanyUser: (body: ICompanyUser) => requests.post(PROFILE_CREATE_COMPANY_USER_URL, body),
    updateCompanyUser: (body: ICompanyUser, id: number | undefined) =>
        requests.put(`${PROFILE_CREATE_COMPANY_USER_URL}/${id}`, body),
    disableCompanyUser: (id: string) => requests.put(`${PROFILE_CREATE_COMPANY_USER_URL}/${id}/disable`, {}),
    deleteCompanyUser: (id: string) => requests.delete(`${PROFILE_CREATE_COMPANY_USER_URL}s/${id}`),
    getPriceList: (id: number): Promise<IPriceList[]> => requests.get(`${PRICE_LIST_URL}/${id}`),
    updatePriceList: (body: IPriceList[]) => requests.put(PRICE_LIST_URL, { priceList: body }),
    getRightsExclusivity: (id: number): Promise<IRightsExclusivity> =>
        requests.get(`${RIGHTS_AND_EXCLUSIVITY_URL}/${id}`),
    updateRightsExclusivity: (id: number, body: IRightsExclusivity): Promise<IRightsExclusivity> =>
        requests.put(`${RIGHTS_AND_EXCLUSIVITY_URL}/${id}`, body),
    checkDisplayName: (displayName: string) => requests.put(CHECK_DISPLAY_NAME, { displayName }),
    checkEmail: (email: string) => requests.put(CHECK_EMAIL, { email }),
    getAgencyCompanyUsers: (params?: any): Promise<ICompanyUser[]> => requests.get(`${AGENCY_USER_URL}s`, params),
    getAgencyCompanyUsersV2: (params?: ICompanyUsersFilter): Promise<BaseModel<ICompanyUser[], ICompanyInfoData>> =>
        requests.get(`${AGENCY_USER_URL}s/v2`, params),
    createAgencyUser: (body: ICompanyUser) => requests.post(AGENCY_USER_URL, body),
    updateAgencyUser: (body: ICompanyUser, id: number | undefined) => requests.put(`${AGENCY_USER_URL}/${id}`, body),
    disableAgencyUser: (id: string) => requests.put(`${AGENCY_USER_URL}/${id}/disable`, {}),
    getAgencyBilling: (): Promise<IBaseModal<ICompany>> => requests.get(AGENCY_COMPANY_URL),
    updateAgencyBilling: (body: ICompany) => requests.put(AGENCY_COMPANY_BILLING_URL, body),
    deleteProfile: (id: number) => requests.delete(`${DELETE_USER_PROFILE_URL}/${id}`),
    getPrivateComment: (id: number) => requests.get(`${PROFILE_PRIVATE_COMMENT_URL}/${id}`),
    putPrivateComment: (id: number, body: { message: string }) =>
        requests.put(`${PROFILE_SEND_COMMENT_URL}/${id}`, body),
    sendCriticalError: (message: string) => requests.put(`${BASE_URL}profile/critical/notify/error`, { message }),
    getGroups: (isClient: boolean, id?: number | string, userId?: number | string): Promise<ICompanyUserGroup[]> =>
        requests.get(`${GROUPS_URL}/${isClient ? 'client' : 'users'}/${id}${userId ? `?userId=${userId}` : ''}`),
    addGroup: (body: IAddGroup, id?: number | string): Promise<ICompanyUserGroup[]> =>
        requests.put(`${GROUPS_URL}/users/${id}`, body),
    getInfluencerDeliveryAddress: (): Promise<IDeliveryAddress> => requests.get(`${BASE_URL}influencer/delivery`),
    addInfluencerDeliveryAddress: (body: IDeliveryAddress): Promise<IDeliveryAddress[]> =>
        requests.put(`${BASE_URL}influencer/delivery`, body),
    getAgencyDeliveryAddress: (): Promise<IDeliveryAddress> => requests.get(`${BASE_URL}admin/agency/delivery`),
    addAgencyDeliveryAddress: (body: IDeliveryAddress): Promise<IDeliveryAddress[]> =>
        requests.put(`${BASE_URL}admin/agency/delivery`, body),
    addAgencyInfluencerDeliveryAddress: (body: IDeliveryAddress, id: number | string): Promise<IDeliveryAddress[]> =>
        requests.put(`${BASE_URL}admin/agency/delivery/user/${id}`, body),
    getAgencyInfluencerDeliveryAddress: (id: number | string): Promise<IDeliveryAddress> =>
        requests.get(`${BASE_URL}admin/agency/delivery/user/${id}`),
    toggleAgencyInfluencerOwnAddress: (id: number | string, useOwnAddress: boolean): Promise<IDeliveryAddress[]> =>
        requests.put(`${BASE_URL}admin/agency/delivery/user/${id}`, { useOwnAddress: useOwnAddress }),
};
